import { useEffect, useRef, useState } from 'react';

/**
 * The welcome screen's glimmer: a soft light that passes over one starter
 * question at a time, so someone who takes this for just another chatbot
 * sees there are real questions waiting. The first set opens on the ads'
 * question ("What if God is real?"), then the others in a random order; a
 * set from "More questions" is all random.
 *
 * It never competes with someone using the page: it runs only while the box
 * is empty and unfocused and no answer is coming, and never with reduced
 * motion. Each pass ends inside five seconds (WCAG 2.2.2: moving content that
 * starts by itself and lasts longer must be pausable), and a pass starts
 * only on arrival, on a new set, or after a long pause (two at most).
 */
export const GLIMMER = {
  startMs: 700, // let the page settle first
  stepMs: 1000, // one question to the next
  lengthMs: 1300, // one sheen (ChatView.css cv-sheen-*)
  idleMs: [25000, 45000], // replays while nothing happens, then never again
};

/** The order a pass visits the starters: the first, then the rest shuffled; or all shuffled. */
export function glimmerOrder(count, pinnedFirst, random = Math.random) {
  const rest = Array.from({ length: count }, (_, i) => i).slice(pinnedFirst ? 1 : 0);
  for (let i = rest.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [rest[i], rest[j]] = [rest[j], rest[i]];
  }
  return pinnedFirst && count > 0 ? [0, ...rest] : rest;
}

function reducedMotion() {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {
    return false;
  }
}

/** The index of the starter glimmering now, or -1. A new `run` starts a new pass. */
export function useStarterGlimmer({ count, active, pinnedFirst, run }) {
  const [index, setIndex] = useState(-1);
  // Each run plays once: focusing the box and leaving it doesn't start it over.
  const played = useRef(null);
  useEffect(() => {
    if (!active || !count || reducedMotion() || played.current === run) {
      setIndex(-1);
      return undefined;
    }
    played.current = run;
    const order = glimmerOrder(count, pinnedFirst);
    const timers = order.map((i, k) => setTimeout(() => setIndex(i), GLIMMER.startMs + k * GLIMMER.stepMs));
    timers.push(setTimeout(() => setIndex(-1), GLIMMER.startMs + (order.length - 1) * GLIMMER.stepMs + GLIMMER.lengthMs));
    return () => {
      timers.forEach(clearTimeout);
      setIndex(-1);
    };
  }, [active, count, pinnedFirst, run]);
  return index;
}
